// documentationManager.util.ts

import { db, FirebaseTimestamp } from '../../config/main_config'; // Adjust the path as needed
import { createToast } from './createToast.util'; // Utility for showing toast notifications
import { waitForCompanyID } from './waitForCompanyID.util';

/**
 * Interface representing a Policy.
 */
interface Policy {
    id: string;
    policyName: string;
}

/**
 * Interface representing a Supporting Document.
 */
interface SupportingDocument {
    id: string;
    fileName: string;
}

/**
 * Interface representing a Key Document.
 */
interface KeyDocument {
    id: string;
    cardTitle: string;
}

/**
 * Interface representing all Documentation Data.
 */
interface DocumentationData {
    policies: Policy[];
    supportingDocuments: SupportingDocument[];
    keyDocuments: KeyDocument[];
}

/**
 * Fetches policies data from Firestore.
 * 
 * @returns A promise that resolves to an array of Policy objects.
 */
export async function fetchPolicies(): Promise<Policy[]> {
    const companyID = await waitForCompanyID();
    const policiesCollection = db
        .collection('customers')
        .doc(companyID)
        .collection('product')
        .doc('policyPackages')
        .collection('policies');

    try {
        const snapshot = await policiesCollection.get();
        const policies: Policy[] = snapshot.docs.map(doc => ({
            id: doc.id,
            policyName: doc.data().policy_name || 'Unnamed Policy',
        }));
        return policies;
    } catch (error) {
        console.error('Error fetching policies:', error);
        createToast({
            message: 'Failed to load policies.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return [];
    }
}

/**
 * Fetches supporting documents data from Firestore.
 * 
 * @returns A promise that resolves to an array of SupportingDocument objects.
 */
export async function fetchSupportingDocuments(): Promise<SupportingDocument[]> {
    const companyID = await waitForCompanyID();
    const otherDocsDoc = db
        .collection('customers')
        .doc(companyID)
        .collection('product')
        .doc('policyPackages')
        .collection('ISO27001keyDocuments')
        .doc('otherDocs');

    try {
        const doc = await otherDocsDoc.get();
        if (doc.exists && doc.data()?.uploaded) {
            const uploaded = doc.data()?.uploaded;
            const supportingDocuments: SupportingDocument[] = Object.keys(uploaded).map(key => ({
                id: key,
                fileName: uploaded[key].fileName || 'Unnamed Document',
            }));
            return supportingDocuments;
        } else {
            console.warn('No supporting documents found.');
            return [];
        }
    } catch (error) {
        console.error('Error fetching supporting documents:', error);
        createToast({
            message: 'Failed to load supporting documents.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return [];
    }
}

/**
 * Fetches key documents data from Firestore.
 * 
 * @returns A promise that resolves to an array of KeyDocument objects.
 */
export async function fetchKeyDocuments(): Promise<KeyDocument[]> {
    const companyID = await waitForCompanyID();
    const keyDocumentsCollection = db
        .collection('customers')
        .doc(companyID)
        .collection('product')
        .doc('policyPackages')
        .collection('ISO27001keyDocuments');

    try {
        const snapshot = await keyDocumentsCollection.get();
        const keyDocuments: KeyDocument[] = snapshot.docs.map(doc => ({
            id: doc.id,
            cardTitle: doc.id === 'ismsSoa' ? 'Statement of Applicability' : doc.data().cardDetails?.cardTitle || 'Unnamed Key Document',
        }));
        return keyDocuments;
    } catch (error) {
        console.error('Error fetching key documents:', error);
        createToast({
            message: 'Failed to load key documents.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return [];
    }
}

/**
 * Renders the documentation data into a formatted HTML string.
 * 
 * @param documentationData - The DocumentationData object containing policies, supporting documents, and key documents.
 * @returns A formatted HTML string representing the documentation.
 */
export function renderDocumentation(documentationData: DocumentationData): string {
    const { policies, supportingDocuments, keyDocuments } = documentationData;

    let html = `
        <div class="documentation-container">
            <section class="documentation-section">
                <b>Policies</b>
                <ul>
    `;

    if (policies.length > 0) {
        policies.forEach(policy => {
            html += `<li>${policy.policyName}</li>`;
        });
    } else {
        html += `<li>No Policies Found.</li>`;
    }

    html += `
                </ul>
            </section>
            
            <section class="documentation-section">
                <b>Supporting Documents</b>
                <ul>
    `;

    if (supportingDocuments.length > 0) {
        supportingDocuments.forEach(doc => {
            html += `<li>${doc.fileName}</li>`;
        });
    } else {
        html += `<li>No Supporting Documents Found.</li>`;
    }

    html += `
                </ul>
            </section>
            
            <section class="documentation-section">
                <b>Key Documents</b>
                <ul>
    `;

    if (keyDocuments.length > 0) {
        keyDocuments.forEach(doc => {
            html += `<li>${doc.cardTitle}</li>`;
        });
    } else {
        html += `<li>No Key Documents Found.</li>`;
    }

    html += `
                </ul>
            </section>
        </div>
    `;

    return html;
}

/**
 * initialises the Documentation Manager by fetching all necessary data and rendering the documentation.
 * 
 * @returns A promise that resolves to a formatted HTML string representing the documentation.
 */
export async function initialiseDocumentation(): Promise<string> {
    try {
        const [policies, supportingDocuments, keyDocuments] = await Promise.all([
            fetchPolicies(),
            fetchSupportingDocuments(),
            fetchKeyDocuments()
        ]);

        const documentationData: DocumentationData = {
            policies,
            supportingDocuments,
            keyDocuments
        };

        const renderedHTML = renderDocumentation(documentationData);
        return renderedHTML;
    } catch (error) {
        console.error('Error initializing documentation:', error);
        createToast({
            message: 'Failed to initialise documentation.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return '<p>Failed to load documentation.</p>';
    }
}

/**
 * Sets up a real-time listener for documentation data using onSnapshot.
 * 
 * @param callback - A callback function that receives the rendered HTML string whenever data changes.
 */
export function setupDocumentationListener(callback: (renderedHTML: string) => void): void {
    waitForCompanyID().then(companyID => {
        const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
        const docRef = db.doc(docPath);

        docRef.onSnapshot(async docSnapshot => {
            if (docSnapshot.exists) {
                const policies = await fetchPolicies();
                const supportingDocuments = await fetchSupportingDocuments();
                const keyDocuments = await fetchKeyDocuments();

                const documentationData: DocumentationData = {
                    policies,
                    supportingDocuments,
                    keyDocuments
                };

                const renderedHTML = renderDocumentation(documentationData);
                callback(renderedHTML);
            } else {
                console.warn('Documentation document does not exist.');
                callback('<p>No documentation available.</p>');
            }
        }, error => {
            console.error('Error with onSnapshot listener for documentation:', error);
            createToast({
                message: 'Real-time updates for documentation failed.',
                customStyles: {
                    backgroundColor: '#ff0000',
                    color: '#ffffff'
                }
            });
        });
    });
}